/**
 * Auth Context
 *
 * Provides authentication state to the entire app.
 * Calls GET /auth/me on mount to check session status.
 * Tokens are never exposed — only user info and CSRF token.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { setCsrfToken } from '@semantical/services/authenticatedFetch';

export interface AuthWorkspace {
    id: string;
    name: string;
}

export interface AuthUser {
    userId: string;
    email?: string;
    name?: string;
    roles: string[];
    organizationId?: string;
    organizationName?: string;
    workspaces: AuthWorkspace[];
}

export interface AuthContextValue {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    csrfToken: string | null;
    organization: { id: string; name: string; slug: string } | null;
    login: (returnUrl?: string) => void;
    logout: () => Promise<void>;
    refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [csrfToken, setCsrf] = useState<string | null>(null);
    const [organization, setOrganization] = useState<{ id: string; name: string; slug: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchAuth = useCallback(async () => {
        try {
            const res = await fetch('/auth/me', {
                credentials: 'include',
            });

            if (res.ok) {
                const data = await res.json();
                setUser({ ...data.user, workspaces: data.workspaces || [] });
                setCsrf(data.csrfToken);
                setCsrfToken(data.csrfToken);
                setOrganization(data.organization ?? null);
            } else {
                setUser(null);
                setCsrf(null);
                setCsrfToken(null);
                setOrganization(null);
            }
        } catch {
            setUser(null);
            setCsrf(null);
            setCsrfToken(null);
            setOrganization(null);
        }
    }, []);

    const refreshAuth = useCallback(async () => {
        await fetchAuth();
    }, [fetchAuth]);

    useEffect(() => {
        let cancelled = false;

        async function checkAuth() {
            await fetchAuth();
            if (cancelled) return;
            setIsLoading(false);
        }

        checkAuth();

        return () => {
            cancelled = true;
        };
    }, [fetchAuth]);

    const login = useCallback((returnUrl?: string) => {
        const url = returnUrl || window.location.pathname + window.location.search;
        window.location.href = `/auth/login?return_url=${encodeURIComponent(url)}`;
    }, []);

    const logout = useCallback(async () => {
        try {
            const headers: Record<string, string> = {};
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }

            const res = await fetch('/auth/logout', {
                method: 'POST',
                credentials: 'include',
                headers,
            });

            if (res.ok) {
                const data = await res.json();
                if (data.logoutUrl) {
                    window.location.href = data.logoutUrl;
                    return;
                }
            }
        } catch {
            // Logout failed, redirect to home
        }

        setUser(null);
        setCsrf(null);
        setCsrfToken(null);
        window.location.href = '/';
    }, [csrfToken]);

    const value: AuthContextValue = {
        user,
        isAuthenticated: !!user,
        isLoading,
        csrfToken,
        organization,
        login,
        logout,
        refreshAuth,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
